export default class FormSubmissionModule {
  constructor({
    formSaveUrl,
    fileType,
    formVersionId,
    formUserId,
    formStackId = null,
    images = [],
    notifications,
    stackIsFinalUrl = null,
    qcForm = null,
    controller = null,
  }) {
    this.fileType = fileType;
    this.formSaveUrl = formSaveUrl;
    this.formVersionId = formVersionId;
    this.formUserId = formUserId;
    this.formStackId = formStackId;
    this.images = images;
    this.notifications = notifications;
    this.stackIsFinalUrl = stackIsFinalUrl;
    this.qcForm = qcForm;
    this.controller = controller;
  }

  collectImagesFromSelector(selector, imageArray) {
    document.querySelectorAll(selector).forEach((input) => {
      if (input.value !== "" && !imageArray.includes(input.value)) {
        this.images.push(input.value);
      }
    });
  }

  async submitForm(
    submission,
    method = "PATCH",
    successCallback = null,
    errorCallback = null,
    draftStatus = null,
    formType = "qc_form",
    formStatus = null
  ) {
    try {
      // Common parameters
      const commonParams = {
        responses: submission,
        form_version_id: this.formVersionId,
        user_id: this.formUserId,
        images: this.images,
      };

      // Specific parameters for different form types
      let specificParams = {};

      switch (formType) {
        case "grading_form":
          specificParams = {
            grading_form: {
              ...commonParams,
              disclaimer: submission.isAgreed,
              status: formStatus,
              stack_id: this.formStackId,
            },
          };
          break;
        case "adjudication":
          specificParams = {
            adjudication: {
              ...commonParams,
              status: formStatus,
            },
          };
          break;
        case "other_grading_form":
          specificParams = {
            other_grading_form: {
              ...commonParams,
              disclaimer: submission.isAgreed,
              status: formStatus,
              stack_id: this.formStackId,
            },
          };
          break;
        case "other_adjudication":
          specificParams = {
            other_adjudication: {
              ...commonParams,
              status: formStatus,
            },
          };
          break;
        case "eligibility_form":
          specificParams = {
            eligibility_form: {
              ...commonParams,
              status: formStatus,
              stack_id: this.formStackId,
            },
          };
          break;
        default:
          specificParams = commonParams; // Default to qc_form
          break;
      }
      let url = this.formSaveUrl.replace(/\/$/, ""); // Strip trailing "/"
      if (formType === "grading_form") {
        const existGradingForm =
          await this.controller.checkIfGradingFormExists();

        if (existGradingForm.exists) {
          const gradingId = existGradingForm.id;

          // Check if URL already contains '/grading_forms'
          if (url.includes("/grading_forms")) {
            // URL already has '/grading_forms' somewhere, so extract the base URL before '/grading_forms'
            url = url.split("/grading_forms")[0];
          } else {
            // Just clean up any JSON extensions if present
            url = url.replace(/\.json$/, "");
          }

          // Append the correct path once
          url = `${url}/grading_forms/${gradingId}.json`;
          method = "PATCH";
        }
      }
      // Use `url` and `method` for the request
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          "X-CSRF-Token": document.head
            .querySelector('meta[name="csrf-token"]')
            .getAttribute("content"),
        },
        method: method,
        body: JSON.stringify(specificParams),
      });

      this.notifications.hideLoader();
      if (response.status === 200) {
        if (formStatus === "completed") {
          this.notifications.success("Form submitted successfully!");
        }
        if (formStatus === "completed" || formStatus === "editing") {
          location.reload();
        }
        if (typeof successCallback === "function") successCallback();
      } else {
        this.notifications.error();
        if (typeof errorCallback === "function")
          errorCallback("Something went wrong with form submissions!");
      }
    } catch (error) {
      this.notifications.error();
      if (typeof errorCallback === "function")
        errorCallback("Something went wrong with form submissions!");
    }
  }

  async formCommit(
    e,
    isFinal,
    buttonSelector,
    releaseEyeTarget,
    gradabilityTarget,
    releaseComments,
    releaseReason,
    qcFormImageViewer,
    checkImagesLiterality,
    checkLiteralityWithReleaseEye,
    formStatus = null,
    draftStatus = null
  ) {
    e.preventDefault();
    const button = document.querySelector(buttonSelector);
    this.notifications.showLoader();
    const release_eye = releaseEyeTarget?.querySelector('input[name="release_eye"]:checked') || null;

    const gradable = { value: "processed" };

    if (!release_eye && formStatus === "completed") {
      Swal.fire({
        icon: "warning",
        title: "Action Required",
        text: "Please choose one of the answer to release / not release the form to the graders.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });
      this.notifications.hideLoader();
      return false;
    }
    const selectedReleaseEye = release_eye?.value || "";
    // HOLD is validated like NA (do not release); server maps HOLD -> NA on stack
    // and stores HOLD on query_management.release_decision for QMS.
    const releaseEyeValue = selectedReleaseEye;
    const doesNotReleaseToGraders =
      releaseEyeValue === "NA" || releaseEyeValue === "HOLD";
    const fileTypesArray = (this.fileType || "").split(",");
    const allFilesArePDFs = fileTypesArray.every(
      (type) => type.toLowerCase() === "pdf"
    );

    if (
      !doesNotReleaseToGraders &&
      checkImagesLiterality &&
      !checkImagesLiterality() &&
      qcFormImageViewer === "true" &&
      formStatus === "completed"
    ) {
      if (!allFilesArePDFs) {
        alert("No image selected for literality.");
        this.notifications.hideLoader();
        button.classList.remove("hidden");
        return;
      }
    }

    if (
      (releaseEyeValue === "OD" || releaseEyeValue === "OS") &&
      checkLiteralityWithReleaseEye &&
      !checkLiteralityWithReleaseEye() &&
      qcFormImageViewer === "true" &&
      formStatus === "completed"
    ) {
      alert("Release eye must match any of the image literality!");
      this.notifications.hideLoader();
      button.classList.remove("hidden");
      return;
    }
    if (isFinal || draftStatus) {
      try {
        const response = await fetch(this.stackIsFinalUrl, {
          headers: {
            "Content-Type": "application/json; charset=utf-8",
            "X-CSRF-Token": document.head
              .querySelector('meta[name="csrf-token"]')
              .getAttribute("content"),
          },
          method: "PATCH",
          body: JSON.stringify({
            status: formStatus,
            gradable: gradable.value,
            release_eye: releaseEyeValue,
            release_comment:
              releaseComments?.value?.trim?.() || "",
            release_reason:
              releaseReason?.value?.trim?.() || "",
            user_id: this.formUserId,
          }),
        });
        this.notifications.hideLoader();
        button.classList.remove("hidden");
        if (formStatus === "editing") {
          location.reload();
        }
        return true;
      } catch (error) {
        // Revert stack status if form submission fails
        await fetch(this.stackIsFinalUrl, {
          headers: {
            "Content-Type": "application/json; charset=utf-8",
            "X-CSRF-Token": document.head
              .querySelector('meta[name="csrf-token"]')
              .getAttribute("content"),
          },
          method: "PATCH",
          body: JSON.stringify({
            status: "initiated",
            gradable: "processed",
            release_eye: "",
            release_comment: "",
            release_reason: "",
            user_id: this.formUserId,
          }),
        });

        this.notifications.hideLoader();
        button.classList.remove("hidden");
        console.error("Error during commit:", error);
      }
    } else if (!draftStatus && !isFinal) {
      alert("You must agree to terms");
      this.notifications.hideLoader();
      return false;
    }
  }

  checkIfAllImagesUploaded(submission, dataQuestionArray) {
    const allImagesAreUploaded = true;
    const uploadedFilesObj = submission.files;

    if (uploadedFilesObj && dataQuestionArray.length > 0) {
      for (const key of dataQuestionArray) {
        if (!uploadedFilesObj.hasOwnProperty(key)) {
          this.uploadImageForQuestion(key);
          this.notifications.hideLoader();
          return false; // Exit early if any image is missing
        }
      }
    } else if (!uploadedFilesObj && dataQuestionArray.length > 0) {
      this.notifications.error("Please Upload screenshots!");
      this.notifications.hideLoader();
      return false;
    }

    return allImagesAreUploaded;
  }

  uploadImageForQuestion(key) {
    Swal.fire({
      icon: "warning",
      title: "Please Upload screenshots for " + key,
      showConfirmButton: true,
    });
  }
}
